import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { 
  Trophy, 
  Calendar, 
  Clock, 
  MapPin, 
  ChevronRight,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Live Badge
const LiveBadge = () => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-red-500 text-white rounded-full">
    <span className="w-1 h-1 bg-white rounded-full animate-pulse"></span>
    LIVE
  </span>
);

// Mobile Fixture Card
const MobileFixtureCard = ({ fixture, onClick }) => (
  <div
    onClick={onClick}
    className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] active:scale-[0.98] transition-transform cursor-pointer"
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] text-gray-500">{fixture.date} • {fixture.time}</span>
      {fixture.is_live && <LiveBadge />}
    </div>
    
    <div className="flex items-center justify-between gap-2 mb-2">
      <div className="flex-1 flex items-center gap-1.5 min-w-0">
        <Trophy className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        <span className="font-medium text-white text-xs truncate">{fixture.home_team}</span>
      </div>
      <span className="text-[10px] text-gray-600 flex-shrink-0">vs</span>
      <div className="flex-1 flex items-center gap-1.5 min-w-0 justify-end">
        <span className="font-medium text-white text-xs truncate">{fixture.away_team}</span>
        <Trophy className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
      </div>
    </div>
    
    <div className="flex items-center justify-between text-[10px] text-gray-500">
      <span className="flex items-center gap-1 truncate max-w-[60%]">
        <MapPin className="w-3 h-3" />
        {fixture.venue}
      </span>
      <span className="font-bold text-[#0055FF]">D{fixture.ticket_price}</span>
    </div>
  </div>
);

// Desktop Fixture Card
const DesktopFixtureCard = ({ fixture, onClick }) => (
  <motion.div
    whileHover={{ scale: 1.01 }}
    whileTap={{ scale: 0.99 }}
    onClick={onClick}
    className="group flex items-center justify-between p-4 rounded-xl bg-white/[0.03] hover:bg-white/[0.05] border border-white/[0.06] hover:border-[#0055FF]/30 cursor-pointer transition-all"
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className="font-medium text-white text-sm">{fixture.home_team}</span>
        <span className="text-xs text-gray-600">vs</span>
        <span className="font-medium text-white text-sm">{fixture.away_team}</span>
        {fixture.is_live && <LiveBadge />}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-gray-500">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {fixture.date}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {fixture.time}
        </span>
        <span className="flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          <span className="truncate max-w-[100px]">{fixture.venue}</span>
        </span>
      </div>
    </div>
    <div className="flex items-center gap-3 ml-3 flex-shrink-0">
      <div className="text-right">
        <span className="text-[10px] text-gray-600">From</span>
        <p className="text-sm font-bold text-[#0055FF]">D{fixture.ticket_price}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#0055FF]" />
    </div>
  </motion.div>
);

// Zone Card
const ZoneCard = ({ zone, fixtures, onFixtureClick, onViewAll, isMobile }) => (
  <div className="bg-gradient-to-b from-white/[0.03] to-white/[0.01] rounded-xl border border-white/[0.06] overflow-hidden">
    {/* Header */}
    <div className="flex items-center justify-between p-3 sm:p-4 border-b border-white/[0.05]">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-xl sm:text-2xl">
          {zone.flag_icon || '🇬🇲'}
        </div>
        <div>
          <h3 className="font-semibold text-white text-sm sm:text-base">{zone.name}</h3>
          <p className="text-[10px] sm:text-xs text-gray-500">{zone.fixtures_count || fixtures.length} fixtures</p>
        </div>
      </div>
      <button
        onClick={onViewAll}
        className="flex items-center gap-1 text-xs text-[#0055FF]"
      >
        <span className="hidden sm:inline">View All</span>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>

    {/* Fixtures */}
    <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
      {fixtures.length > 0 ? (
        fixtures.slice(0, 2).map((fixture) => (
          isMobile ? (
            <MobileFixtureCard
              key={fixture.id}
              fixture={fixture}
              onClick={() => onFixtureClick(fixture.id)}
            />
          ) : (
            <DesktopFixtureCard
              key={fixture.id}
              fixture={fixture}
              onClick={() => onFixtureClick(fixture.id)}
            />
          )
        ))
      ) : (
        <div className="text-center py-4 sm:py-6">
          <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-gray-700 mx-auto mb-2" />
          <p className="text-xs sm:text-sm text-gray-600">No fixtures</p>
        </div>
      )}
    </div>
  </div>
);

export default function FootballPage() {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [zonesWithFixtures, setZonesWithFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const zonesRes = await axios.get(`${API_URL}/api/tournaments?category=football&active_only=true`);
        const zonesData = zonesRes.data;
        
        const zonesWithFixturesData = await Promise.all(
          zonesData.map(async (zone) => {
            try {
              const fixturesRes = await axios.get(`${API_URL}/api/fixtures?tournament_id=${zone.id}&limit=2`);
              return { ...zone, fixtures: fixturesRes.data || [] };
            } catch (e) {
              return { ...zone, fixtures: [] };
            }
          })
        );
        
        setZones(zonesData);
        setZonesWithFixtures(zonesWithFixturesData);
      } catch (error) {
        console.error('Failed to fetch football data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] overflow-x-hidden">
      <Navbar />
      
      {/* Hero */}
      <section className="relative py-10 sm:py-14 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-48 sm:w-64 h-48 sm:h-64 bg-emerald-500/10 rounded-full blur-[80px]" />
          <div className="absolute bottom-1/4 right-1/4 w-48 sm:w-64 h-48 sm:h-64 bg-[#0055FF]/10 rounded-full blur-[80px]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
            <Trophy className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-emerald-400 text-xs font-medium">Football</span>
          </div>
          
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3">
            Gambian Football
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-[#0055FF]">
              Live Matches
            </span>
          </h1>
          
          <p className="text-gray-400 text-sm sm:text-base max-w-md mx-auto">
            Get tickets to matches across all zones
          </p>
        </div>
      </section>

      {/* Zones */}
      <section className="py-6 sm:py-10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <h2 className="text-base sm:text-xl font-bold text-white">Browse by Zone</h2>
              <p className="text-[11px] sm:text-sm text-gray-500">Select a zone to see fixtures</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-[#0055FF] animate-spin" />
            </div>
          ) : zonesWithFixtures.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {zonesWithFixtures.map((zone, index) => (
                <motion.div
                  key={zone.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <ZoneCard
                    zone={zone}
                    fixtures={zone.fixtures}
                    onFixtureClick={(fixtureId) => navigate(`/fixture/${fixtureId}`)}
                    onViewAll={() => navigate(`/football/${zone.id}`)}
                    isMobile={isMobile}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Trophy className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-1">No Zones</h3>
              <p className="text-gray-500 text-sm">Zones will appear when added by admin.</p>
            </div>
          )}
        </div>
      </section>

      {/* Quick Zone Nav */}
      {zones.length > 0 && (
        <section className="py-6 sm:py-10 bg-[#0c0c0c]">
          <div className="max-w-7xl mx-auto px-4">
            <h3 className="text-sm sm:text-base font-semibold text-white mb-4 text-center">Quick Access</h3>
            
            {/* Mobile: Horizontal Scroll */}
            <div className="sm:hidden overflow-x-auto scrollbar-hide -mx-4 px-4">
              <div className="flex gap-2 pb-2">
                {zones.map((zone) => (
                  <Link
                    key={zone.id}
                    to={`/football/${zone.id}`}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-xs font-medium active:scale-95 transition-transform"
                  >
                    <span>{zone.flag_icon || '🇬🇲'}</span>
                    <span>{zone.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Desktop: Centered Flex */}
            <div className="hidden sm:flex flex-wrap justify-center gap-2">
              {zones.map((zone) => (
                <Link
                  key={zone.id}
                  to={`/football/${zone.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-emerald-500/30 text-white text-sm font-medium transition-all"
                >
                  <span>{zone.flag_icon || '🇬🇲'}</span>
                  <span>{zone.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 bg-[#080808] border-t border-white/[0.05]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-600 text-xs">© 2026 Gam Ticket. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
